// ============================================================
// SURFACE EFFECTS - what the ground does to whoever stands on it.
//
// A zone used to carry `going` and a `hazard` boolean that NOTHING read:
// it was a note to the GM and a colour on the map. This is the join
// between painted ground and the combat engine.
//
// THE TRICK IS THAT A SURFACE GRANTS CONDITION CODES. In this app a
// condition code is already several things at once:
//
//   Pen:agi=-10   a test modifier `conditionModifiers.ts` applies
//   Pen:movement=-10  a movement penalty `conditionMovement.ts` applies
//   Fi            On Fire, which `getTurnEffects` turns into 1d10 E
//                 ignoring AP, prompted by the turn tracker
//
// So "−10 Agility in the mire" and "burning while you stand in the
// promethium" are the same mechanism, and DAMAGE-ON-YOUR-TURN NEEDED NO
// DAMAGE SYSTEM - it needed the surface to grant `Fi`.
//
// Nothing here is stored. Standing in mud is a relation between a
// combatant and the ground, not a property of the combatant, so this is
// derived from where they stand and ends the instant they leave. That is
// the same conclusion cover reached in v7.51.0, for the same reason.
//
// Pure. Zero React dependency.
// ============================================================
import { zoneCellKey } from './zones';
import type { Zone } from './zones';
import type { TerrainRules } from './traitRules';

export interface SurfaceEffect {
  /** Condition codes granted while standing here. */
  grants: string[];
  /** Trait flags granted while standing here - undying, hardy, … */
  traits: Record<string, boolean | number>;
  /**
   * The best cover the ground itself offers, as a PAIR. Never cumulative,
   * and never a level from one surface with an AP from another.
   */
  coverAp: number;
  coverLevel: 'none' | 'low' | 'half' | 'threeQuarter' | 'full';
  /** The surfaces responsible, so the sheet can say where this came from. */
  labels: string[];
}

const NOTHING: SurfaceEffect = { grants: [], traits: {}, coverAp: 0, coverLevel: 'none', labels: [] };

/** Shared constant - identity matters to the memoised UI that reads it. */
export const NO_SURFACE = NOTHING;

/**
 * Does this surface reach this combatant's feet?
 *
 * A flyer is over the mire and a burrower is under it, so by default
 * neither is touched - the same exemptions `traitRules.ts` already
 * describes, rather than a second opinion about them. A SURFACE MAY SAY
 * OTHERWISE: a warp rift or a null field is not something you fly over,
 * and `affects` is how a GM says so, per surface.
 */
/** Movement traits a surface can lend, and what they mean to the ground. */
const MOVEMENT_GIFTS: Record<string, keyof TerrainRules> = {
  flyer: 'ignoresTerrain',
  phase: 'ignoresTerrain',
  incorporeal: 'ignoresTerrain',
  hoverer: 'ignoresGoing',
  burrower: 'canBurrow',
};

/** Does this surface lend a trait that changes how ground is crossed? */
function grantsMovement(zone: Zone): boolean {
  return Object.keys(zone.traits ?? {}).some((t) => MOVEMENT_GIFTS[t] && zone.traits![t]);
}

/**
 * These rules, plus whatever the ground underfoot is lending.
 *
 * Returns the SAME OBJECT when nothing is lent, so the caller can skip
 * the second pass entirely - which is the common case by far.
 */
function terrainRulesWith(rules: TerrainRules, zones: Zone[]): TerrainRules {
  const lent = zones.flatMap((zone) => giftedFields(zone, rules));
  if (!lent.length) return rules;
  const out = { ...rules } as unknown as Record<string, unknown>;
  for (const field of lent) out[field] = true;
  return out as unknown as TerrainRules;
}

/** The movement rules this zone's traits switch on that are not already on. */
function giftedFields(zone: Zone, rules: TerrainRules): (keyof TerrainRules)[] {
  return Object.entries(MOVEMENT_GIFTS)
    .filter(([trait, field]) => zone.traits?.[trait] && rules[field] !== true)
    .map(([, field]) => field);
}

export function catchesFeet(zone: Zone, rules: TerrainRules): boolean {
  const affects = zone.affects ?? {};
  if (rules.ignoresTerrain && !affects.flying) return false;
  if (rules.canBurrow && !affects.burrowing) return false;
  // Hovering skims the ground: it escapes what merely SLOWS or fouls the
  // footing, which is what `ignoresGoing` already means elsewhere.
  if (rules.ignoresGoing && !affects.hovering) return false;
  return true;
}

/**
 * What the ground at this point does to a combatant of these rules.
 *
 * Coordinates are METRES; `cellSize` converts. Several surfaces may
 * overlap and all of them apply - except cover, which takes the highest,
 * because cover is never cumulative anywhere else in this app either.
 */
export function surfaceAt(
  x: number,
  z: number,
  zones: Zone[] | undefined,
  rules: TerrainRules,
  cellSize = 1,
): SurfaceEffect {
  if (!zones?.length) return NOTHING;

  const size = cellSize > 0 ? cellSize : 1;
  const key = zoneCellKey(Math.floor(x / size), Math.floor(z / size));

  // Every surface here that catches this combatant AS THEY ARE.
  const here = zones.filter((zone) => zone.cells?.includes(key) && catchesFeet(zone, rules));

  // A SURFACE NEVER EXEMPTS YOU FROM ITSELF.
  //
  // A pad granting Hovering is circular - hovering is exactly what
  // decides whether ground catches you. The rule chosen at the table:
  // what a surface gives you works on all OTHER ground, never on the
  // surface giving it. So the granted traits are collected first, and
  // every surface is then re-asked with them EXCEPT the ones that gave
  // them. No loop is possible, because the second pass never grants.
  const lifted = terrainRulesWith(rules, here);
  const applies = lifted === rules ? here : here.filter((zone) => grantsMovement(zone) || catchesFeet(zone, lifted));

  const effect = combinedEffect(applies);
  return isNothing(effect) ? NOTHING : effect;
}

/** What every applying surface does, together. */
function combinedEffect(applies: Zone[]): SurfaceEffect {
  const effect: SurfaceEffect = { grants: [], traits: {}, coverAp: 0, coverLevel: 'none', labels: [] };
  for (const zone of applies) {
    // Two surfaces granting the same thing is one grant, not two: a
    // doubled `Pen:agi=-10` would silently become −20.
    addOnce(effect.grants, zone.grants ?? []);
    // A trait switched OFF used to be stored as `false`, and `false` then
    // spread onto the combatant over their own number (v8.310.8). Only a
    // value that means something is lent.
    for (const [field, value] of Object.entries(zone.traits ?? {})) if (value) effect.traits[field] = value;
    takeCover(effect, zone);
    if (zone.label) addOnce(effect.labels, [zone.label]);
  }
  return effect;
}

/** Append each item the list does not already hold. */
function addOnce(list: string[], items: string[]): void {
  for (const item of items) if (!list.includes(item)) list.push(item);
}

/**
 * The better cover wins, AP first, later surfaces winning ties. The whole
 * pair moves together, or the winner ends up with one surface's level and
 * another's AP.
 */
function takeCover(effect: SurfaceEffect, zone: Zone): void {
  if (!zone.coverLevel || zone.coverLevel === 'none') return;
  if (!((zone.coverAp ?? 0) >= effect.coverAp)) return;
  effect.coverAp = zone.coverAp ?? 0;
  effect.coverLevel = zone.coverLevel;
}

/** Does this surface do nothing at all? */
function isNothing(effect: SurfaceEffect): boolean {
  if (effect.grants.length || effect.labels.length) return false;
  return effect.coverLevel === 'none' && Object.keys(effect.traits).length === 0;
}
